import { computed, ref } from 'vue'
import { githubApi } from '@/api/github'
import { apiErrorMessage } from '@/lib/errors'
import { withAsyncState } from '@/lib/asyncState'
import type { GithubInstallation } from '@/types/models'

// Singleton: shared between the Integrations page and the Settings > Repositories
// tab so connecting/disconnecting in one place is reflected in the other.
const installations = ref<GithubInstallation[]>([])
const loading = ref(false)
const refreshing = ref(false)
const error = ref<string | null>(null)
const initialized = ref(false)
let fetchPromise: Promise<void> | null = null

export function useGithubInstallations() {
  async function fetch(force = false): Promise<void> {
    if (initialized.value && !force) return
    if (fetchPromise && !force) return fetchPromise

    fetchPromise = withAsyncState(
      error,
      'Failed to load GitHub installations.',
      async () => {
        installations.value = (await githubApi.installations()) ?? []
        initialized.value = true
      },
      loading,
    ).then(() => {
      fetchPromise = null
    })

    return fetchPromise
  }

  // Repositories are persisted in the API's DB; this re-syncs them from
  // GitHub before re-fetching.
  async function refresh(installationId: string): Promise<void> {
    await withAsyncState(
      error,
      'Failed to refresh repositories.',
      async () => {
        await githubApi.syncRepositories(installationId)
        await fetch(true)
      },
      refreshing,
    )
  }

  async function connect(): Promise<void> {
    await withAsyncState(error, 'Failed to start the GitHub connection.', async () => {
      window.location.href = await githubApi.installUrl()
    })
  }

  async function update(id: string, attrs: Partial<Pick<GithubInstallation, 'openrouter_api_key' | 'jev_enabled'>>): Promise<boolean> {
    const result = await withAsyncState(error, 'Failed to update the installation.', async () => {
      const updated = await githubApi.updateInstallation(id, attrs)
      const index = installations.value.findIndex((installation) => installation.id === id)
      if (index !== -1) installations.value[index] = updated
      return true
    })
    return result !== undefined
  }

  async function disconnect(id: string): Promise<boolean> {
    const result = await withAsyncState(error, 'Failed to disconnect the installation.', async () => {
      await githubApi.disconnect(id)
      installations.value = installations.value.filter((installation) => installation.id !== id)
      return true
    })
    return result !== undefined
  }

  async function toggleStar(installationId: string, repoFullName: string, isStarred: boolean): Promise<void> {
    error.value = null
    const repo = installations.value
      .find((installation) => installation.id === installationId)
      ?.repositories.find((candidate) => candidate.full_name === repoFullName)
    const previous = repo?.is_starred ?? false
    if (repo) repo.is_starred = isStarred

    try {
      await githubApi.starRepository(installationId, repoFullName, isStarred)
    } catch (e: unknown) {
      if (repo) repo.is_starred = previous
      error.value = apiErrorMessage(e, 'Failed to update starred repository.')
    }
  }

  // Called on logout: `initialized` must be cleared too, not just the data,
  // or the next session's `fetch()` short-circuits and never hits the
  // network — leaving the previous user's installations (and the review
  // runs, repo settings, etc. views that key off them) visible indefinitely.
  function reset(): void {
    installations.value = []
    loading.value = false
    error.value = null
    initialized.value = false
    fetchPromise = null
  }

  return {
    installations: computed(() => installations.value),
    loading: computed(() => loading.value),
    refreshing: computed(() => refreshing.value),
    error: computed(() => error.value),
    fetch,
    refresh,
    connect,
    update,
    disconnect,
    toggleStar,
    reset,
  }
}
